import React from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex, Text } from '@atlaskit/primitives';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { N0, R300 } from '@atlaskit/theme/colors';
import SectionMessage, { Appearance } from '@atlaskit/section-message';
import { NEW_DESIGN_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import {
  BillingStatus,
  FetchLinkedEntitlementsPpcQuery,
  FetchLinkedEntitlementsPpcQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  AddOnLozenge,
  CustomPanel,
  DynamicResultTable,
  EntitlementIcon,
  SandboxLozenge,
  withApollo,
} from '@css-xp/base';
import { getProductName } from '@css-xp/base/entitlement-utils';
import { FormattedDate } from '@css-xp/base/formattedV2';
import { NOT_AVAILABLE, EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { emptyStateProp } from '@css-xp/ppc-common';
import { isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { fetchLinkedEntitlementsPPC } from '../../../queries/fetch-linked-entitlements-ppc';
import './linked-entitlements-table.scss';

interface LinkedEntitlementsTableProps {
  entitlementId?: string;
  message?: string;
  messageType?: Appearance;
  header?: React.ReactNode;
  showBillDateColumn?: boolean;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType?: Array<LinkedEntitlementTypes>;
  productType?: LinkedProductTypes;
  parentContainerID?: string;
  checkForLastParentExistence?: boolean;
  findProducts?: boolean;
  findApps?: boolean;
  findAllEntitlements?: boolean;
  updateLinkedEntitlements?: (data: any) => void;
  setLoading?: (data: boolean) => void;
  linkedEntsCount?: (data: any) => void;
  linkedEntitlementsData?: any;
  linkedEntitlementsLoading?: boolean;
  linkedEntitlementsError?: Error;
  transactionAccountId?: string | null;
  hideInfoMessage?: boolean;
  removeAddOnEntitlements?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  productId?: string;
  isJPDAsJiraFamilyEnabled?: boolean;
  isDesignIteration1Enabled?: boolean;
}

export const LinkedEntitlementsTable = ({
  entitlementId = '',
  message,
  messageType = 'warning',
  header,
  parentContainerID,
  showBillDateColumn = false,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findApps = false,
  findProducts = false,
  checkForLastParentExistence = false,
  updateLinkedEntitlements,
  setLoading,
  linkedEntsCount,
  linkedEntitlementsData,
  linkedEntitlementsLoading,
  linkedEntitlementsError,
  transactionAccountId,
  hideInfoMessage = false,
  removeAddOnEntitlements = false,
  isSandboxSupportForSubscriptionsEnabled = false,
  productId,
  isJPDAsJiraFamilyEnabled = false,
  isDesignIteration1Enabled,
}: LinkedEntitlementsTableProps) => {
  let { data, loading, error } = useQuery<FetchLinkedEntitlementsPpcQuery, FetchLinkedEntitlementsPpcQueryVariables>(
    fetchLinkedEntitlementsPPC,
    {
      variables: {
        id: entitlementId,
        searchLinkedEntitlementParams: {
          entitlementId: entitlementId,
          containerId: parentContainerID ?? undefined,
          currentEntitlementType,
          destinationEntitlementType,
          productType,
          findApps,
          findProducts,
          checkForLastParentExistence,
        },
        transactionAccountId,
      },
      displayName: 'fetchAllLinkedEntitlements',
      notifyOnNetworkStatusChange: true,
      skip: linkedEntitlementsData || linkedEntitlementsLoading || linkedEntitlementsError,
    }
  );

  if (linkedEntitlementsData) {
    data = linkedEntitlementsData;
  }

  const linkedEntitlements: Array<LinkedDestinationEntitlement> = React.useMemo(() => {
    let entitlements = data?.entitlementDetails.linkedEntitlements?.filter(
      (linkedEntitlementObj) => linkedEntitlementObj?.billingStatus === BillingStatus.ACTIVE
    );

    if (removeAddOnEntitlements) {
      entitlements = entitlements?.filter((linkedEntitlementObj) => !linkedEntitlementObj?.isAddOn);
    }

    return (entitlements as Array<LinkedDestinationEntitlement>) || [];
  }, [data, removeAddOnEntitlements]);

  const visibleLinkedEnts = React.useMemo(() => {
    let filterLinkedEntsWithoutContainerId;

    const isJiraProduct =
      productType === LinkedProductTypes.JIRA || isProductBelongsToJiraFamily(productId, { isJPDAsJiraFamilyEnabled });
    if (isJiraProduct && parentContainerID) {
      filterLinkedEntsWithoutContainerId = linkedEntitlements.filter(
        (linkedEnt) =>
          !(
            (linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
            (isSandboxSupportForSubscriptionsEnabled && linkedEnt.edition === 'Family') ||
            linkedEnt.entitlementId === parentContainerID
          )
      );
    } else {
      filterLinkedEntsWithoutContainerId = linkedEntitlements.filter(
        (linkedEnt) => !(linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX)
      );
    }
    //filter container subscription if entitlement is add on or a collection product
    if (data?.entitlementDetails?.isAddOn || currentEntitlementType === LinkedEntitlementTypes.COLLECTION_PRODUCT) {
      return linkedEntitlements.filter((linkedEnt) => linkedEnt?.edition !== 'Family');
    }
    return filterLinkedEntsWithoutContainerId;
  }, [linkedEntitlements]);

  React.useEffect(() => {
    setLoading && setLoading(loading);
  }, [loading, setLoading]);

  React.useEffect(() => {
    linkedEntsCount && visibleLinkedEnts.length > 0 && linkedEntsCount(1 + visibleLinkedEnts.length);
  }, [visibleLinkedEnts, linkedEntsCount]);

  React.useEffect(() => {
    updateLinkedEntitlements && linkedEntitlements.length > 0 && updateLinkedEntitlements(linkedEntitlements);
  }, [linkedEntitlements, updateLinkedEntitlements]);

  const getRows = () => {
    return visibleLinkedEnts.map((item: LinkedDestinationEntitlement, index) => {
      const cellData = [
        {
          content: (
            <>
              <EntitlementIcon
                logoId={item?.logoId as string}
                productId={item?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <span className="product-name">{getProductName(item)}</span>
              {item?.isSandbox && <SandboxLozenge />}
              {item?.isAddOn && <AddOnLozenge />}
            </>
          ),
        },
        {
          content: item?.entitlementNumber ? (
            <span className="entitlement-number">{item?.entitlementNumber}</span>
          ) : (
            <span className="entitlement-number" aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        },
      ];

      if (showBillDateColumn) {
        const latestPeriodEndDate = item?.latestPeriodEndDate || item?.subscriptionTimestamps?.lastTrialEndTimestamp;
        cellData.splice(1, 0, {
          content: latestPeriodEndDate ? (
            <FormattedDate date={latestPeriodEndDate as string} dateFormat={NEW_DESIGN_DATE_FORMAT} />
          ) : (
            <span aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        });
      }

      return {
        key: item.entitlementId ? item.entitlementId : index.toString(),
        cells: cellData,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Product',
        shouldTruncate: true,
        width: 30,
      },
      {
        content: 'Entitlement number',
        width: 20,
      },
    ];

    if (showBillDateColumn) {
      cells.splice(1, 0, {
        content: 'Bill date',
        width: 20,
      });
    }

    return {
      cells,
    };
  };

  const renderSubscriptionsAndApps = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Box padding="space.200">
          <Flex direction="column" gap="space.300">
            {visibleLinkedEnts.map((item: LinkedDestinationEntitlement) => {
              return (
                <Flex key={item.entitlementId}>
                  <Flex alignItems="center">
                    <Box paddingInline="space.100">
                      <EntitlementIcon productId={item?.productId} className="avatar medium" isCloud />
                    </Box>
                  </Flex>
                  <Flex direction="column">
                    <Text>
                      <span>{getProductName(item)}</span>
                      {item?.isSandbox && <SandboxLozenge />}
                      {item?.isAddOn && <AddOnLozenge />}
                    </Text>
                    <Text size="small" color="color.text.subtlest">
                      {item?.entitlementNumber ? (
                        <span>{item?.entitlementNumber}</span>
                      ) : (
                        <span aria-label="Not available" data-testid="not-available">
                          {NOT_AVAILABLE}
                        </span>
                      )}
                    </Text>
                    {showBillDateColumn && (
                      <Text size="small" color="color.text.subtlest">
                        {item?.latestPeriodEndDate ? (
                          <FormattedDate date={item?.latestPeriodEndDate} dateFormat={NEW_DESIGN_DATE_FORMAT} />
                        ) : null}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      );
    }

    return (
      <DynamicResultTable
        boxShadow={false}
        testId="subscriptions-and-apps-table"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateProp}
        isLoading={loading || linkedEntitlementsLoading}
      />
    );
  };

  return (
    <div className="linked-entitlements-table-container">
      <div className="linked-ent-info-banner">
        {!hideInfoMessage && (
          <SectionMessage appearance={messageType} testId="linked-ent-info-banner">
            <p>
              {message
                ? message
                : 'All the following subscriptions and the apps will also be affected with this action'}
            </p>
          </SectionMessage>
        )}
      </div>
      {isDesignIteration1Enabled && loading ? (
        <SkeletonLoaderWrapper>
          <SkeletonLoader height="35px" />
        </SkeletonLoaderWrapper>
      ) : (
        <CustomPanel
          testId="linked-ents"
          expandedHeader={header ? header : `View subscriptions`}
          collapsedHeader={header ? header : `View subscriptions`}
          version={isDesignIteration1Enabled ? 3 : 0}
          backgroundColor={N0}
        >
          {error || linkedEntitlementsError ? (
            <div className="error-block">
              <ErrorIcon label="Error" size="medium" primaryColor={R300} />
              <span className="error-text">
                It looks like something went wrong. <span className="n-300">Please go back and try again.</span>
              </span>
            </div>
          ) : (
            renderSubscriptionsAndApps()
          )}
        </CustomPanel>
      )}
    </div>
  );
};

export default withApollo(LinkedEntitlementsTable);
